import React, { useEffect, useMemo, useState } from 'react';
import { format, isValid } from 'date-fns';
import { Box, IconButton, Theme, Typography, Tooltip } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';
import { ErrorOutlineRounded, PauseRounded, PlayArrowRounded } from '@mui/icons-material';
import { FONT_GSS_FUTURA } from 'themes/default';
import { translate } from 'translations/translator';
import { MESSAGE_SENDER as MessageSender } from 'types/enums';
import { remCalc } from 'util/style-util';

export type PredefinedAudioMessageTheme = MessageSender.EDUCATOR | MessageSender.STUDENT;
const t = key => translate('errors', key);
export const safetyArea = 10;

type Props = {
  audioLink?: string;
  fileType?: string;
  predefinedTheme?: PredefinedAudioMessageTheme;
  name?: string;
  details?: string;
  date?: string; // stringified date
  showCurrentAndDurationTimes: boolean;
  numberOfSprites?: number;
  onStartPlaying?: (audio: HTMLAudioElement) => void;
};

type BoxColors = {
  backgroundColor: string;
  progressColor: string;
  remainingProgressColor: string;
};

interface ThemesModel {
  educator: BoxColors;
  student: BoxColors;
}

const getComponentTheme = (theme: any): ThemesModel =>
  ({
    educator: {
      backgroundColor: theme?.palette?.primary?.light,
      progressColor: theme?.palette?.primary?.main,
      remainingProgressColor: theme?.palette?.primary?.lighter
    },
    student: {
      backgroundColor: theme?.palette?.secondary?.lighter,
      progressColor: theme?.palette?.secondary?.dark,
      remainingProgressColor: theme?.palette?.secondary?.light
    }
  } as ThemesModel);

const PlayRecording: React.FC<Props> = ({
  audioLink,
  predefinedTheme,
  name,
  details,
  date,
  showCurrentAndDurationTimes = false,
  numberOfSprites = 53,
  onStartPlaying
}) => {
  const {
    header,
    mainBox,
    spriteParentDirectWrapper,
    spriteParent,
    progressBox,
    clickCatchBox,
    remainingProgressBox,
    spriteOverWrapper,
    spriteDirectWrapper,
    spriteLine,
    footer,
    detailsText,
    contentContainer,
    detailsTextFooterLeft,
    detailsTextFooterRight,
    iconsColorStudent,
    iconsColorTeacher,
    detailsTextTeacher,
    cursorPointer,
    buttonWrapper
  } = useStyles();
  const [audio, setAudio] = useState<HTMLAudioElement>();
  const [progress, setProgress] = useState<number>();
  const [playState, setPlayState] = useState<boolean>();
  const [metadataLoaded, setMetadataLoaded] = useState<boolean>(false);
  const [duration, setDuration] = useState<string>('00:00');
  const [hasError, setHasError] = useState<boolean>(!!audioLink);
  // this will keep the width of the sprites area
  let boxWidth = 0;

  const thm = useTheme();

  /**
   * Make sure that the component is re-rendered using the most recent colors from the material theme
   * after the theme was updated after it was created when refreshing the page.
   * THEME REFRESH - START
   */

  let inUseTheme;
  const [backgroundColor, setBackgroundColor] = useState<string>(inUseTheme?.backgroundColor);
  const [progressColor, setProgressColor] = useState<string>(inUseTheme?.progressColor);
  const [remainingProgressColor, setRemainingProgressColor] = useState<string>(
    inUseTheme?.remainingProgressColor
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const themes = getComponentTheme(thm);
    const inUseTheme =
      predefinedTheme === MessageSender.EDUCATOR ? themes.educator : themes.student;
    setBackgroundColor(inUseTheme.backgroundColor);
    setProgressColor(inUseTheme.progressColor);
    setRemainingProgressColor(inUseTheme.remainingProgressColor);
  });
  /**
   * THEME REFRESH - END
   */

  useEffect(() => {
    if (audioLink && audio?.src !== (audioLink || '').trim() && !audio?.src.endsWith(audioLink)) {
      setHasError(false);
      if (audio) {
        audio.remove();
      }
      const newAudio = new Audio(audioLink);
      newAudio.preload = 'auto';
      newAudio.load();

      newAudio.onloadedmetadata = () => {
        setDuration(!isNaN(newAudio.duration) ? getTimeInMinutes(newAudio.duration) : '00:00:00');
        setMetadataLoaded(true);
      };

      newAudio.onpause = () => {
        setPlayState(false);
      };

      newAudio.onplaying = () => {
        setPlayState(true);
        if (onStartPlaying) {
          onStartPlaying(newAudio);
        }
      };

      newAudio.ontimeupdate = () => {
        setProgress(getProgressWidth(newAudio));
      };

      newAudio.onended = () => {
        setPlayState(false);
        setProgress(getProgressWidth(newAudio));
      };

      newAudio.onerror = () => {
        setHasError(true);
      };

      setAudio(newAudio);
    } else if (!audioLink) {
      setHasError(true);
    }

    return () => {
      if (!audio) {
        return;
      }
      audio.pause();
      audio.src = '';
      audio.remove();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [audioLink, audio]);

  const onPlayPause = (): void => {
    if (!audio) {
      return;
    }

    audio.paused ? audio.play() : audio.pause();
    setProgress(getProgressWidth());
    if (onStartPlaying && !audio.paused) {
      onStartPlaying(audio);
    }
  };

  const getPercentageThrough = (inAudio?: HTMLAudioElement): number => {
    if (!inAudio) {
      return 0;
    }
    const currentTime = inAudio.currentTime;
    const totalTime = inAudio.duration;
    return (currentTime * 100) / totalTime;
  };

  const getProgressWidth = (inAudio: HTMLAudioElement | undefined = audio): number => {
    if (!inAudio) {
      return 0;
    }

    const percentageGone = getPercentageThrough(inAudio);
    const calcWidth = (percentageGone * boxWidth) / 100;
    return !isNaN(calcWidth) || calcWidth < 0 ? calcWidth : 0;
  };

  const memoizedHeights: number[] = useMemo(
    () =>
      new Array(numberOfSprites).fill({}).map(() => Math.max(20, Math.floor(Math.random() * 60))),
    [numberOfSprites]
  );

  const detectClick = (e: React.MouseEvent<HTMLElement>): void => {
    if (!metadataLoaded || hasError) {
      return;
    }
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const spriteWidth = boxWidth;
    const inBoxPosition = e.clientX - rect.left - safetyArea; // x click position within the element.

    if (audio) {
      if (!metadataLoaded) {
        return;
      }

      if (inBoxPosition < 0) {
        audio.currentTime = 0;
        return setProgress(getProgressWidth());
      } else if (inBoxPosition > spriteWidth) {
        audio.currentTime = audio.duration;
        return setProgress(getProgressWidth());
      }

      audio.currentTime = (audio.duration * ((Math.round(inBoxPosition) * 100) / boxWidth)) / 100;
      setProgress(getProgressWidth());
    }
  };

  const getTimeInMinutes = (toFormat: number): string => {
    if (isNaN(toFormat)) {
      return '00:00';
    }
    return format(new Date(0, 0, 0, 0, 0, toFormat), 'mm:ss');
  };

  const getTime = (dateSent: string | undefined): string =>
    !dateSent || !isValid(new Date(dateSent)) ? '--:--' : format(new Date(dateSent), 'h:mm a');

  const getBorderWidth = (height: number, index: number): string => {
    return `${remCalc(Math.round((60 - height) / 2) + 2)} ${remCalc(1)} ${remCalc(
      Math.round((60 - height) / 2) + 2
    )} ${index !== 0 ? remCalc(2) : 0}`;
  };

  const grabSpriteAreaWidth = (elem: HTMLDivElement): void => {
    if (!elem) {
      return;
    }
    boxWidth = elem.getBoundingClientRect().width;
  };

  return (
    <Box className={mainBox} bgcolor={backgroundColor}>
      <Box
        className={header}
        flexDirection={predefinedTheme === MessageSender.EDUCATOR ? 'row' : 'row-reverse'}
      >
        <Box>
          {showCurrentAndDurationTimes && (
            <Typography
              variant="body1"
              classes={{
                root:
                  predefinedTheme !== MessageSender.EDUCATOR
                    ? `${detailsText} ${detailsTextFooterRight}`
                    : `${detailsText} ${detailsTextFooterLeft}`
              }}
            >
              {`${audio ? getTimeInMinutes(audio.currentTime) : '00:00'}/${duration}`}
            </Typography>
          )}
        </Box>
        <Typography
          classes={{
            root:
              predefinedTheme !== MessageSender.EDUCATOR
                ? `${detailsText} ${detailsTextTeacher} ${detailsTextFooterLeft}`
                : `${detailsText} ${detailsTextTeacher} ${detailsTextFooterRight}`
          }}
        >
          {name}
        </Typography>
      </Box>

      <Box className={contentContainer}>
        <Box className={buttonWrapper}>
          {hasError ? (
            <Tooltip title={t('unableToPlayAudio')}>
              <div>
                {/* https://github.com/mui-org/material-ui/issues/8416*/}
                <IconButton
                  disabled={hasError}
                  size="small"
                  onClick={onPlayPause}
                  classes={{
                    root:
                      predefinedTheme !== MessageSender.EDUCATOR
                        ? iconsColorStudent
                        : iconsColorTeacher
                  }}
                >
                  <ErrorOutlineRounded fontSize="inherit" />
                </IconButton>
              </div>
            </Tooltip>
          ) : (
            <IconButton
              disabled={hasError}
              size="small"
              onClick={onPlayPause}
              classes={{
                root:
                  predefinedTheme !== MessageSender.EDUCATOR ? iconsColorStudent : iconsColorTeacher
              }}
            >
              {playState ? (
                <PauseRounded fontSize="inherit" />
              ) : (
                <PlayArrowRounded fontSize="inherit" />
              )}
            </IconButton>
          )}
        </Box>
        {/* Workaround for Material Box element not supporting ref https://github.com/mui-org/material-ui/issues/17010 */}
        <div ref={grabSpriteAreaWidth} className={spriteOverWrapper}>
          <Box className={spriteParentDirectWrapper}>
            <Box
              className={`${clickCatchBox} ${!hasError ? cursorPointer : ''}`}
              onClick={detectClick}
            />
            <Box className={spriteParent}>
              {/*BACKGROUND BAR - REMAINING*/}
              <Box className={remainingProgressBox} bgcolor={remainingProgressColor} />
              {/*BACKGROUND BAR - PROGRESS*/}
              <Box className={progressBox} width={`${progress}px`} bgcolor={progressColor} />
              <Box className={spriteDirectWrapper}>
                {memoizedHeights.map((height: number, index: number) => (
                  <div
                    key={index}
                    className={spriteLine}
                    style={{
                      height: height,
                      borderStyle: 'solid',
                      borderColor: backgroundColor,
                      borderWidth: getBorderWidth(height, index)
                    }}
                  />
                ))}
              </Box>
            </Box>
          </Box>
        </div>
      </Box>
      <Box
        className={footer}
        flexDirection={predefinedTheme === MessageSender.EDUCATOR ? 'row' : 'row-reverse'}
      >
        <Typography
          variant="body1"
          classes={{
            root:
              predefinedTheme !== MessageSender.EDUCATOR
                ? `${detailsText} ${detailsTextFooterRight}`
                : `${detailsText} ${detailsTextFooterLeft}`
          }}
        >
          {getTime(date)}
        </Typography>
        <Typography
          variant="body1"
          classes={{
            root:
              predefinedTheme !== MessageSender.EDUCATOR
                ? `${detailsText} ${detailsTextFooterLeft}`
                : `${detailsText} ${detailsTextFooterRight}`
          }}
        >
          <span>{details}</span>
        </Typography>
      </Box>
    </Box>
  );
};

export default PlayRecording;

const useStyles = makeStyles((theme: Theme) => {
  return {
    header: {
      width: remCalc('100%'),
      display: 'flex',
      justifyContent: 'space-between',
      alignContent: 'center'
    },
    mainBox: {
      height: 'auto',
      width: remCalc(420),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'start',
      position: 'relative',
      borderRadius: remCalc(24),
      paddingTop: remCalc(16),
      paddingBottom: remCalc(16),
      paddingRight: 0,
      paddingLeft: 0
    },
    contentContainer: {
      display: 'flex',
      justifyContent: 'space-evenly',
      alignContent: 'center',
      alignItems: 'center',
      width: '100%',
      padding: `0 ${remCalc(10)} 0 ${remCalc(10)}`
    },
    buttonWrapper: {
      margin: `0 ${remCalc(15)} 0 0`
    },
    spriteParentDirectWrapper: {
      display: 'inline-block',
      position: 'relative'
    },
    spriteParent: {
      width: 'auto',
      height: remCalc(60),
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: 'transparent',
      zIndex: 0
    },
    clickCatchBox: {
      height: remCalc(60),
      position: 'absolute',
      top: '0',
      // allow a greater flexibility for rewinding to start on mobile by extending the get_to_start and get_to_end area- START
      left: `${-safetyArea}px`,
      right: `${-safetyArea}px`,
      // allow a greater flexibility for rewinding to start on mobile by extending the get_to_start and get_to_end area - START
      zIndex: 2,
      // disable the highlight of spriteArea happening on touch on Android
      '-webkit-tap-highlight-color': 'rgba(0,0,0,0)'
    },
    cursorPointer: {
      cursor: 'pointer'
    },
    progressBox: {
      height: remCalc('100%'),
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: -1,
      fontFamily: FONT_GSS_FUTURA
    },
    remainingProgressBox: {
      width: remCalc('100%'),
      height: remCalc('100%'),
      zIndex: -2,
      position: 'absolute',
      top: 0,
      left: 0
    },
    spriteOverWrapper: {
      display: 'flex'
    },
    spriteDirectWrapper: {
      width: 'auto',
      height: remCalc(60),
      overflow: 'hidden',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: 'transparent',
      zIndex: 1,
      boxSizing: 'content-box'
    },
    spriteLine: {
      backgroundColor: 'transparent',
      display: 'inline-block',
      boxSizing: 'content-box',
      width: remCalc(3)
    },
    footer: {
      fontSize: remCalc(14),
      width: remCalc('100%'),
      display: 'flex',
      justifyContent: 'space-between',
      alignContent: 'center'
    },
    inlineBlock: {
      display: 'inline-block'
    },
    detailsText: {
      fontSize: remCalc(14),
      fontWeight: 300
    },
    detailsTextTeacher: {
      fontWeight: 800
    },
    detailsTextFooterLeft: {
      marginLeft: remCalc(80)
    },
    detailsTextFooterRight: {
      marginRight: remCalc(20)
    },
    iconsColorStudent: () => ({
      fontSize: remCalc(48),
      '&>span': {
        color: theme.palette.secondary.dark
      }
    }),
    iconsColorTeacher: () => ({
      fontSize: remCalc(48),
      '&>span': {
        color: theme.palette.primary.main
      }
    }),
    iconsColorError: {
      fontSize: remCalc(48),
      '&>span': {
        color: 'lightgray'
      }
    },
    errorContainer: () => ({
      width: remCalc('100%'),
      height: remCalc('100%'),
      backgroundColor: `#C0C0C050`,
      color: theme.palette.primary.main,
      borderRadius: remCalc(16),
      fontSize: remCalc(24),
      display: 'flex',
      alignContent: 'center',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: 0,
      left: 0,
      zIndex: 3
    })
  };
});
